import React, { useContext } from 'react'
import { Switch, Route, Redirect, useParams } from 'react-router-dom'
import { Location, History } from 'history'
import BarcodesReportPage from 'reports/barcodes'
import DiscountsReportPage from 'reports/discounts'
import SalesReportPage from 'reports/sales'
import EngageToolsStatsPage from 'engageTools/stats/index'
import { Feature, useHasFeature, withFeatures } from 'features'
import { StatsService } from 'http/statsService'
import { EngageToolsService } from 'engageTools/engageToolsService'
import { RequestService } from 'requests/requestService'
import { LoginService } from 'http/loginService'
import { LoggingService } from 'http/loggingService'
import { InventoryServiceContext } from 'inventory/context'
import Superset from './superset'
import { BaseRouteParams } from 'hocs'
import { SigmaReport, SigmaService } from './sigma'
import ReportsSectionTabs, { useReportsSections } from './sectionTabs'

interface ReportsHomeProps {
    history: History
    location: Location
    statsService: StatsService
    engageToolsService: EngageToolsService
    requestService: RequestService
    backofficeEndpoint: string
    loginService: LoginService
    loggingService: LoggingService
    sigmaService: SigmaService
    hasFeature: (feature: string, accountSlug?: string) => boolean
}

function ReportsHome(props: ReportsHomeProps) {
    const inventoryService = useContext(InventoryServiceContext)
    const { accountSlug } = useParams<BaseRouteParams>()
    const sections = useReportsSections()
    const fallbackHomePage = `/account/${accountSlug}/reports/${
        useHasFeature('ReportsOverview') ? 'overview' : 'sales'
    }`
    const reportsHomePage = sections[0]?.path ?? fallbackHomePage

    return (
        <>
            <ReportsSectionTabs />
            <Switch location={props.location}>
                <Route path="/account/:accountSlug/reports/superset/:dashboardId" render={() => <Superset />} />
                <Route
                    path="/account/:accountSlug/reports/sales"
                    render={() => (
                        <Feature name="SalesAndFinanceReport" accountSlug={accountSlug}>
                            <SigmaReport
                                sigmaService={props.sigmaService}
                                accountSlug={accountSlug}
                                reportId="14eNWRn8HsWBk7f7Yp5C92"
                            />
                        </Feature>
                    )}
                />
                <Route
                    path="/account/:accountSlug/reports/sales-demo"
                    render={() => (
                        <Feature name="SalesAndFinanceReportDemo" accountSlug={accountSlug}>
                            <SigmaReport
                                sigmaService={props.sigmaService}
                                accountSlug={accountSlug}
                                reportId="5LPOIjzQXvASosUqkgIX5t"
                            />
                        </Feature>
                    )}
                />
                <Route
                    path="/account/:accountSlug/reports/operations"
                    render={() => (
                        <Feature name="OperationsReport" accountSlug={accountSlug}>
                            <SigmaReport
                                sigmaService={props.sigmaService}
                                accountSlug={accountSlug}
                                reportId="5XDFrXuwUfFdjzn3CUcoml"
                            />
                        </Feature>
                    )}
                />
                <Route
                    path="/account/:accountSlug/reports/marketing"
                    render={() => (
                        <Feature name="MarketingReport" accountSlug={accountSlug}>
                            <SigmaReport
                                sigmaService={props.sigmaService}
                                accountSlug={accountSlug}
                                reportId="5mi0s5fSLcKF1mK1KEzcss"
                            />
                        </Feature>
                    )}
                />
                <Route
                    path="/account/:accountSlug/reports/sales-old"
                    render={() => (
                        <Feature name="sale_reports" accountSlug={accountSlug}>
                            <SalesReportPage
                                statsService={props.statsService}
                                loginService={props.loginService}
                                loggingService={props.loggingService}
                                accountSlug={accountSlug}
                            />
                        </Feature>
                    )}
                />
                <Route
                    path="/account/:accountSlug/reports/barcodes"
                    render={() => (
                        <Feature name="BarcodesReport" accountSlug={accountSlug}>
                            <BarcodesReportPage
                                statsService={props.statsService}
                                accountSlug={accountSlug}
                                inventoryService={inventoryService}
                            />
                        </Feature>
                    )}
                />
                <Route
                    path="/account/:accountSlug/reports/discount_codes"
                    render={() => (
                        <Feature name="DiscountCodesReport" accountSlug={accountSlug}>
                            <DiscountsReportPage
                                statsService={props.statsService}
                                backofficeEndpoint={props.backofficeEndpoint}
                                loginService={props.loginService}
                                loggingService={props.loggingService}
                                accountSlug={accountSlug}
                            />
                        </Feature>
                    )}
                />
                <Route
                    path="/account/:accountSlug/reports/engage_tools"
                    render={() => (
                        <Feature name="EngageToolsReport" accountSlug={accountSlug}>
                            <EngageToolsStatsPage accountSlug={accountSlug} />
                        </Feature>
                    )}
                />
                <Route render={() => <Redirect to={reportsHomePage} />} />
            </Switch>
        </>
    )
}

export default withFeatures(ReportsHome)
